import { Card, Dropdown, Field, Toggle } from '@/design-system';
import type { PdfBacklog } from '@/entities/report';
import {
    linkValidityLabel,
    linkValidUntil,
    REPORT_LINK_DAYS,
    type TenantSettings,
    type TenantSettingsPatch,
} from '@/entities/tenant';
import { formatDayLong } from '@/shared/lib';

import { reportCopy, settingsCopy } from '../copy';

export interface ReportSettingsCardProps {
    settings: TenantSettings;
    onChange: (patch: TenantSettingsPatch) => void;
    canManage: boolean;
    disabled?: boolean;
    /** Of verstuurde rapporten op hun PDF wachten; zonder antwoord geen melding. */
    pdf?: PdfBacklog | null;
    /** Alleen voor tests: "vandaag". */
    now?: Date;
}

type ReportToggleKey = 'showCostsOnPublicReport' | 'allowForwarding' | 'requireAccessCode';

const ROWS: ReadonlyArray<{ key: ReportToggleKey; label: string; description: string }> = [
    {
        key: 'showCostsOnPublicReport',
        label: reportCopy.costs,
        description: reportCopy.costsDescription,
    },
    {
        key: 'allowForwarding',
        label: reportCopy.forwarding,
        description: reportCopy.forwardingDescription,
    },
    {
        key: 'requireAccessCode',
        label: reportCopy.accessCode,
        description: reportCopy.accessCodeDescription,
    },
];

/**
 * Wat een nieuwe rapportlink standaard krijgt, met dezelfde namen als op de
 * klantpagina, waar je het per link aanpast.
 *
 * De geldigheid is in dagen (30 tot 3650) en de tekst eronder rekent met de
 * termijn die nu is ingesteld: tot welke dag een link werkt die je vandaag deelt.
 *
 * Kan de server geen PDF's maken, dan staat dat hier bovenaan: de melding onder
 * de PDF-knop verwijst naar Instellingen.
 */
export function ReportSettingsCard({
    settings,
    onChange,
    canManage,
    disabled = false,
    pdf = null,
    now,
}: ReportSettingsCardProps) {
    const days = settings.reportLinkValidDays;
    const options = REPORT_LINK_DAYS.includes(days as (typeof REPORT_LINK_DAYS)[number])
        ? [...REPORT_LINK_DAYS]
        : [...REPORT_LINK_DAYS, days].sort((a, b) => a - b);
    const locked = disabled || !canManage;

    return (
        <Card padding="lg" as="section" aria-labelledby="report-settings-title">
            <h2
                id="report-settings-title"
                className="font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {reportCopy.title}
            </h2>
            <p className="mt-1 mb-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {reportCopy.intro}
            </p>

            {pdf && (pdf.setupRequired || pdf.waiting > 0) ? (
                /* Amber, geen rood: er is niets kapot aan het rapport, alleen aan de PDF-dienst. */
                <div className="my-3 rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint-2)] px-3 py-[10px]">
                    <p className="m-0 text-[13px] leading-[1.4] font-medium text-[var(--warn-text)]">
                        {pdf.setupRequired ? reportCopy.pdfSetupTitle : reportCopy.pdfWaitingTitle}
                    </p>
                    <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--warn-text)]">
                        {pdf.setupRequired
                            ? reportCopy.pdfSetupBody(pdf.waiting)
                            : reportCopy.pdfWaitingBody(pdf.waiting)}
                    </p>
                </div>
            ) : null}

            {ROWS.map((row) => (
                <Toggle
                    key={row.key}
                    className="border-b border-[var(--border-subtle-2)]"
                    label={row.label}
                    description={row.description}
                    checked={settings[row.key]}
                    disabled={locked}
                    onCheckedChange={(checked) => onChange({ [row.key]: checked })}
                />
            ))}

            <div className="pt-[14px]">
                <Field
                    label={reportCopy.validity}
                    description={reportCopy.validityUntil(
                        linkValidityLabel(days),
                        formatDayLong(linkValidUntil(days, now)),
                    )}
                    size="md"
                >
                    <Dropdown
                        value={String(days)}
                        disabled={locked}
                        options={options.map((option) => ({
                            value: String(option),
                            label: linkValidityLabel(option),
                        }))}
                        onChange={(value) => onChange({ reportLinkValidDays: Number(value) })}
                    />
                </Field>
            </div>

            {canManage ? null : (
                <p className="mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {settingsCopy.readOnly}
                </p>
            )}
        </Card>
    );
}
